/**
 * SubscriptionSuccess — /subscription/success
 *
 * Landed here from Stripe after a successful checkout. By the time the user
 * reaches this page, the `checkout.session.completed` webhook has (usually)
 * already arrived at our backend and updated the user row. We poll
 * /subscriptions/status a few times with backoff, because occasionally the
 * webhook arrives a moment after the browser redirect.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { Sparkles } from "lucide-react";

export default function SubscriptionSuccess() {
    const [status, setStatus] = useState(null);
    const [phase, setPhase] = useState("settling");
    const provider = new URLSearchParams(window.location.search).get("provider") || "stripe";
    const providerName = provider === "paypal" ? "PayPal" : "Stripe";

    useEffect(() => {
        let cancelled = false;
        // Exponential-ish backoff: 400ms, 900ms, 1800ms, 3500ms, 6000ms.
        // We stop as soon as the DB reflects an active subscription.
        const delays = [400, 900, 1800, 3500, 6000];
        (async () => {
            for (const d of delays) {
                if (cancelled) return;
                try {
                    const { data } = await api.get("/subscriptions/status");
                    if (data.active) {
                        if (!cancelled) {
                            setStatus(data);
                            setPhase("ready");
                        }
                        return;
                    }
                } catch { /* keep polling */ }
                await new Promise((r) => setTimeout(r, d));
            }
            if (!cancelled) setPhase("delayed");
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="grain min-h-screen" data-testid="subscription-success-page">
            <Header minimal />
            <main className="max-w-2xl mx-auto px-6 pt-24 pb-32 text-center animate-fade-in">
                <div className="flex justify-center mb-10">
                    <BrandMark size={72} ceremonial breathing />
                </div>
                <Sparkles className="mx-auto mb-6 text-ink-gold" size={22} />
                <p className="label-eyebrow mb-4">Abbonamento attivato</p>

                {phase === "ready" && status ? (
                    <>
                        <h1 className="h-serif text-5xl md:text-6xl leading-[1.05] mb-8">
                            Grazie. Sei con <span className="italic text-ink-gold">{planName(status.tier)}</span>.
                        </h1>
                        <p className="text-ink-textMid text-lg leading-[1.7] max-w-lg mx-auto mb-10">
                            Tutto pronto. Puoi tornare al tuo spazio e creare o riaprire le presenze
                            custodite. La ricevuta arriverà via email da {providerName}.
                        </p>
                        <Link to="/dashboard" className="btn-primary" data-testid="success-dashboard">
                            Torna alle tue presenze
                        </Link>
                    </>
                ) : phase === "delayed" ? (
                    <>
                        <h1 className="h-serif text-4xl md:text-5xl leading-[1.1] mb-6">
                            Il pagamento è arrivato.
                        </h1>
                        <p className="text-ink-textMid text-lg leading-[1.7] max-w-lg mx-auto mb-8">
                            Stiamo ancora attivando il piano nel tuo account — normalmente bastano
                            pochi secondi. Se tra un minuto non vedi lo stato attivo, scrivici a
                            <a href="mailto:os.otherside.app@gmail.com" className="text-ink-gold underline underline-offset-4 ml-1">os.otherside.app@gmail.com</a>.
                        </p>
                        <Link to="/dashboard" className="btn-secondary" data-testid="success-dashboard-delayed">Vai al tuo spazio</Link>
                    </>
                ) : (
                    <>
                        <h1 className="h-serif text-4xl md:text-5xl leading-[1.1] mb-6">
                            Stiamo attivando il tuo piano…
                        </h1>
                        <p className="text-ink-textLow italic font-serif">qualche istante</p>
                    </>
                )}
            </main>
        </div>
    );
}

function planName(tier) {
    return ({ basic: "Basic", emotional: "Emotional", premium: "Premium" })[tier] || tier || "";
}
